<template>
  <!--
    Unified usage pill: one compact control combining the conversation-size
    meter (context window fill) and the monthly-allowance meter. Collapsed
    it shows two stacked mini-bars + a terse label; clicking opens a
    popover with the full meters and a one-line explanation of each —
    including WHY they're related (long conversations burn the allowance
    faster). Renders nothing until either metric exists.
  -->
  <v-menu
    v-if="hasAnything"
    offset-y
    bottom
    left
    :close-on-content-click="false"
    max-width="340"
  >
    <template #activator="{ on, attrs }">
      <button type="button" class="aum-pill" :title="pillTitle" v-bind="attrs" v-on="on">
        <span class="aum-bars">
          <span class="aum-bar">
            <span
              class="aum-bar-fill"
              :class="contextLevel"
              :style="{ width: contextPct + '%' }"
            />
          </span>
          <span class="aum-bar">
            <span
              class="aum-bar-fill"
              :class="allowanceLevel"
              :style="{ width: allowancePct + '%' }"
            />
          </span>
        </span>
        <span class="aum-label">{{ pillLabel }}</span>
      </button>
    </template>

    <v-card class="aum-pop pa-4">
      <div class="aum-section">
        <div class="aum-section-title">Conversation size</div>
        <ContextMeter :usage="usage" />
        <p v-if="!usage" class="aum-caption">Not measured yet — send a message first.</p>
        <p class="aum-caption">
          How much of this chat's working memory is in use. Atlas re-reads the whole
          conversation on every message, so long conversations use the allowance faster —
          starting a new chat resets this.
        </p>
      </div>
      <template v-if="allowance">
        <v-divider class="my-3" />
        <div class="aum-section">
          <div class="aum-section-title">
            {{ isUserScope ? 'Personal monthly allowance' : 'Monthly allowance' }}
          </div>
          <AllowanceMeter :allowance="allowance" />
          <p v-if="isUserScope" class="aum-caption">
            Your personal Atlas usage allowance for this month. It resets at the start of
            each month; at 100% Atlas pauses for you until then.
          </p>
          <p v-else class="aum-caption">
            Your organisation's Atlas usage allowance for this month. It resets at the start
            of each month; at 100% Atlas pauses until then.
          </p>
        </div>
      </template>
    </v-card>
  </v-menu>
</template>

<script>
import { computed, defineComponent } from 'vue';
import ContextMeter from '@/components/agent/ContextMeter.vue';
import AllowanceMeter from '@/components/agent/AllowanceMeter.vue';

export default defineComponent({
  name: 'AtlasUsageMeter',
  components: { ContextMeter, AllowanceMeter },
  props: {
    /** ChatResponse.context_usage shape (or null). */
    usage: { type: Object, default: null },
    /** ChatResponse.usage_allowance shape (or null). */
    allowance: { type: Object, default: null },
  },
  setup(props) {
    const hasAnything = computed(() => !!props.usage || !!props.allowance);

    // allowance.scope names the binding budget: 'user' = this user's own
    // cap, anything else = the whole organisation's.
    const isUserScope = computed(() => props.allowance?.scope === 'user');

    const contextPct = computed(() => {
      const u = props.usage;
      if (!u || !u.context_window) return 0;
      return Math.min(100, Math.round((u.total_tokens / u.context_window) * 100));
    });
    // Same thresholds as ContextMeter so collapsed and expanded agree.
    const contextLevel = computed(() =>
      contextPct.value >= 90 ? 'is-danger' : contextPct.value >= 70 ? 'is-warn' : 'is-ok',
    );

    const allowancePct = computed(() => {
      const a = props.allowance;
      if (!a || typeof a.pct_used !== 'number') return 0;
      return Math.min(100, Math.max(0, Math.round(a.pct_used)));
    });
    const allowanceLevel = computed(() => {
      const a = props.allowance;
      if (!a) return 'is-off';
      return a.blocked ? 'is-danger' : a.warn ? 'is-warn' : 'is-ok';
    });

    function formatTokens(n) {
      const v = Number(n) || 0;
      if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
      return String(v);
    }

    const pillLabel = computed(() => {
      const parts = [];
      if (props.usage) parts.push(formatTokens(props.usage.total_tokens));
      if (props.allowance) parts.push(`${allowancePct.value}%`);
      return parts.join(' · ');
    });

    const pillTitle = computed(() => {
      const lines = ['Atlas usage — click for detail.'];
      if (props.usage) {
        lines.push(
          `Conversation size: ${props.usage.total_tokens.toLocaleString()} / ${props.usage.context_window.toLocaleString()} tokens (${contextPct.value}%).`,
        );
      }
      if (props.allowance) {
        lines.push(
          `${isUserScope.value ? 'Personal monthly allowance' : 'Monthly allowance'}: ${props.allowance.pct_used}% used.`,
        );
      }
      return lines.join('\n');
    });

    return {
      hasAnything,
      isUserScope,
      contextPct,
      contextLevel,
      allowancePct,
      allowanceLevel,
      pillLabel,
      pillTitle,
    };
  },
});
</script>

<style scoped>
.aum-pill {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 4px 10px;
  border-radius: 9999px;
  border: none;
  background: rgb(243 244 246);
  cursor: pointer;
  font: inherit;
}
.aum-pill:hover {
  background: rgb(229 231 235);
}
.aum-bars {
  display: inline-flex;
  flex-direction: column;
  gap: 2px;
}
.aum-bar {
  width: 64px;
  height: 4px;
  border-radius: 9999px;
  background: rgb(229 231 235);
  overflow: hidden;
  display: block;
}
.aum-bar-fill {
  display: block;
  height: 100%;
  border-radius: 9999px;
  transition: width 0.4s ease, background-color 0.4s ease;
}
.aum-bar-fill.is-ok {
  background: rgb(79 70 229);
}
.aum-bar-fill.is-warn {
  background: rgb(217 119 6);
}
.aum-bar-fill.is-danger {
  background: rgb(220 38 38);
}
.aum-bar-fill.is-off {
  background: transparent;
}
.aum-label {
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  color: rgb(75 85 99);
  white-space: nowrap;
}
.aum-pop {
  font-size: 12px;
}
.aum-section-title {
  font-size: 12px;
  font-weight: 600;
  color: rgb(55 65 81);
  margin-bottom: 6px;
}
.aum-caption {
  margin: 6px 0 0 0;
  font-size: 11px;
  line-height: 1.45;
  color: rgb(107 114 128);
}
</style>
